import React, { useMemo } from 'react'
import { hasFields, capitalize } from './functions'
import { CommonLabeledDataRow } from '../components/labeled-data-row'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'

type ObjectRowsProps = {
  object: { [key: string]: any }
  enableCopy?: boolean
  checkHash?: boolean
  lengthLimit?: number
  className?: string
}
export function ObjectRows(props: Readonly<ObjectRowsProps>): JSX.Element | null {
  const { object, enableCopy = false, checkHash = false, lengthLimit = 0, className } = props
  const { _t } = useTypedTranslation()

  if (!hasFields(object)) {
    return null
  }

  return (
    <div className={'data-block ' + (className ?? '')}>
      {Object.keys(object).map((key) => {
        const label = capitalize(_t(key))
        const value = object[key]

        return (
          <CommonLabeledDataRow
            key={key}
            label={label}
            value={value}
            enableCopy={enableCopy}
            checkHash={checkHash}
            lengthLimit={lengthLimit}
          />
        )
      })}
    </div>
  )
}

export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

type NestedObjectRendererProps = {
  data: Record<string, any>
  depth?: number
}

/**
 * Turn object into nested data rows, with better visual support for nested objects and arrays
 *
 * @param data
 */
export function NestedObjectRenderer({ data, depth = 0 }: NestedObjectRendererProps): JSX.Element | null {
  const { primitiveEntries, objectEntries } = useMemo(() => {
    const primitiveEntries: [string, any][] = []
    const objectEntries: [string, any][] = []

    for (const [key, value] of Object.entries(data ?? {})) {
      if (value !== null && typeof value === 'object' && !isStringArray(value)) {
        objectEntries.push([key, value])
      } else {
        primitiveEntries.push([key, value])
      }
    }

    return { primitiveEntries, objectEntries }
  }, [data])

  if (!data || typeof data !== 'object') {
    return null
  }

  return (
    <div className={depth > 0 ? 'ms-3 ps-2 border-start' : undefined}>
      {primitiveEntries.length > 0 && renderObjectToDataRows(Object.fromEntries(primitiveEntries))}
      {objectEntries.map(([key, value]) => (
        <div key={key} className="mt-2">
          <div className="label fw-semibold text-capitalize mb-1">{key.replace(/_/g, ' ')}:</div>
          {Array.isArray(value) ? (
            value.map((item: any, idx: number, arr: any[]) => (
              <div key={idx} className={`ps-2 py-2 ${idx < arr.length - 1 ? 'border-bottom' : ''}`}>
                {typeof item === 'object' && item !== null ? (
                  <NestedObjectRenderer data={item} depth={depth + 1} />
                ) : (
                  <span className="value text-break">{String(item)}</span>
                )}
              </div>
            ))
          ) : (
            <NestedObjectRenderer data={value} depth={depth + 1} />
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * Turn object into a list of data-rows elements
 *
 * @param object
 */
export function renderObjectToDataRows(
  object: string | { [key: string]: any } | undefined,
  enableCopy = false,
  checkHash = false,
  lengthLimit = 1024
): JSX.Element | null {
  if (typeof object !== 'object' || !hasFields(object)) {
    return null
  }

  return (
    <div className="data-block">
      {Object.entries(object).map(([key, value]) => {
        const label = capitalize(key)

        return (
          <CommonLabeledDataRow
            key={key}
            label={label}
            value={value}
            enableCopy={enableCopy}
            checkHash={checkHash}
            lengthLimit={lengthLimit}
          />
        )
      })}
    </div>
  )
}
